import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  DragDropContext, Droppable, Draggable, DropResult,
  DroppableProvided, DraggableProvided,
} from '@hello-pangea/dnd';
import { OpportunityService } from '../../services/opportunity.service';
import {
  OpportunityBoard, OpportunityCard, OpportunityStage,
  OPPORTUNITY_STAGES, OPPORTUNITY_STAGE_LABELS,
} from '../../interfaces/crm/crm.interface';
import OpportunityForm from './OpportunityForm';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

// Droppable ids ARE the stage names, so mapping is identity with a safety check.
export const mapDroppableIdToStage = (droppableId: string): OpportunityStage =>
  OPPORTUNITY_STAGES.find(s => s === droppableId) ?? 'NEW';

// Factory used by both the component and the unit test: returns an onDragEnd
// handler that calls changeStage when a card moves to a different column.
// `requestLostReason` (optional) is asked before a card lands in Lost; resolving to null
// cancels the move. `onError` lets the caller tell the user why a move did not stick.
export interface DragEndOptions {
  requestLostReason?: () => Promise<string | null>;
  onError?: (error: unknown) => void;
}

export const buildDragEndHandler = (reload: () => void, options: DragEndOptions = {}) =>
  async (result: DropResult): Promise<void> => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    const stage = mapDroppableIdToStage(destination.droppableId);
    let lostReason: string | undefined;
    if (stage === 'LOST' && options.requestLostReason) {
      const reason = await options.requestLostReason();
      if (reason === null) return; // cancelled: the card stays where it was
      lostReason = reason.trim() || undefined;
    }
    try {
      await OpportunityService.changeStage(draggableId, lostReason ? { stage, lostReason } : { stage });
      reload();
    } catch (e) {
      options.onError?.(e);
      // On failure, reload to restore the authoritative server state.
      reload();
    }
  };

const formatAgo = (iso?: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(iso);
};

const PipelineBoard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [board, setBoard] = useState<OpportunityBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const canManage = usePermission(PERMISSIONS.CRM_MANAGE);

  // Lost-reason prompt: the drag handler awaits this promise.
  const [lostPromptOpen, setLostPromptOpen] = useState(false);
  const [lostReasonText, setLostReasonText] = useState('');
  const lostResolver = useRef<((reason: string | null) => void) | null>(null);
  const requestLostReason = useCallback(() => new Promise<string | null>(resolve => {
    lostResolver.current = resolve;
    setLostReasonText('');
    setLostPromptOpen(true);
  }), []);
  const settleLostPrompt = (reason: string | null) => {
    setLostPromptOpen(false);
    lostResolver.current?.(reason);
    lostResolver.current = null;
  };

  // The toast context value is a fresh object on every provider render; keep `load` stable so a
  // toast appearing does not re-fetch the board (and cannot loop the loading spinner).
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const load = useCallback(async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await OpportunityService.board(institutionId);
      setBoard(res);
    } catch (e) {
      toastRef.current.showApiError(e, 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      void buildDragEndHandler(load, {
        requestLostReason,
        onError: e => toast.showApiError(e, 'Could not move the deal'),
      })(result);
    },
    [load, requestLostReason, toast],
  );

  // Build a stage→cards map so every stage column renders even if absent from the response.
  const cardsByStage: Record<OpportunityStage, OpportunityCard[]> = {
    NEW: [], QUALIFIED: [], PROPOSAL: [], NEGOTIATION: [], WON: [], LOST: [],
  };
  (board?.columns ?? []).forEach(col => { cardsByStage[col.stage] = col.cards; });

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="crm.pipeline.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Pipeline</Typography>
        {canManage && (
          <Button data-help="crm.pipeline.add" variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            Add deal
          </Button>
        )}
      </Box>

      <DragDropContext onDragEnd={onDragEnd}>
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, alignItems: 'flex-start' }}>
          {OPPORTUNITY_STAGES.map(stage => (
            <Droppable droppableId={stage} key={stage}>
              {(provided: DroppableProvided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  data-testid={`column-${stage}`}
                  sx={{
                    minWidth: 260, width: 260, flexShrink: 0,
                    bgcolor: 'grey.100', borderRadius: 1, p: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle2">{OPPORTUNITY_STAGE_LABELS[stage]}</Typography>
                    <Chip size="small" label={cardsByStage[stage].length} />
                  </Box>
                  {cardsByStage[stage].map((card, index) => {
                    const lastActivity = formatAgo(card.lastActivityAt);
                    return (
                      <Draggable draggableId={card.id} index={index} key={card.id} isDragDisabled={!canManage}>
                        {(dragProvided: DraggableProvided) => (
                          <Card
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            onClick={() => navigate(`/crm/opportunities/${card.id}`)}
                            sx={{ mb: 1, cursor: 'pointer' }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{card.title}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {card.customerName ?? '—'}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="caption">
                                  {card.estimatedAmount != null ? formatMoney(card.estimatedAmount, 'BDT') : '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {card.expectedCloseDate ? formatDate(card.expectedCloseDate) : ''}
                                </Typography>
                              </Box>
                              {lastActivity && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Last: ${lastActivity}`}
                                  data-testid={`last-activity-badge-${card.id}`}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          ))}
        </Box>
      </DragDropContext>

      <Dialog open={lostPromptOpen} onClose={() => settleLostPrompt(null)} fullWidth maxWidth="xs" data-help-page="crm.pipeline.lostDialog">
        <DialogTitle>Mark as Lost</DialogTitle>
        <DialogContent data-help-section="crm.pipeline.lostDialog.section.reason">
          <TextField
            data-help="crm.pipeline.lostDialog.reason"
            autoFocus
            fullWidth
            label="Why was this deal lost?"
            value={lostReasonText}
            onChange={e => setLostReasonText(e.target.value)}
            multiline
            minRows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-help="crm.pipeline.lostDialog.cancel" onClick={() => settleLostPrompt(null)}>Cancel</Button>
          <Button data-help="crm.pipeline.lostDialog.confirm" variant="contained" color="error" onClick={() => settleLostPrompt(lostReasonText)}>Mark Lost</Button>
        </DialogActions>
      </Dialog>

      <OpportunityForm
        open={formOpen}
        opportunity={null}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); }}
      />
    </Box>
  );
};

export default PipelineBoard;
